import { err, ok, type Result } from 'neverthrow';

import { TimeInMilliseconds } from ':constants/time';
import { waitFor } from ':helpers/wait-for';

export enum RetryError {
    STILL_ERROR_AFTER_RETRIES = 'STILL_ERROR_AFTER_RETRIES',

    /** Returned if the predicate shouldRetryError has returned false */
    SHOULD_NOT_RETRY_AFTER_ERROR = 'SHOULD_NOT_RETRY_AFTER_ERROR',

    /** Returned if the predicate shouldRetrySuccess has returned false */
    SHOULD_NOT_RETRY_AFTER_SUCCESS = 'SHOULD_NOT_RETRY_AFTER_SUCCESS',
}

export type RetryErrorObject<R, E> =
    | { error: RetryError.STILL_ERROR_AFTER_RETRIES; lastResult: Result<R, E> }
    | { error: RetryError.SHOULD_NOT_RETRY_AFTER_ERROR; lastResult: Result<R, E> }
    | { error: RetryError.SHOULD_NOT_RETRY_AFTER_SUCCESS; lastResult: Result<R, E> };

const BackoffStrategy = {
    CONSTANT: 'constant',
    LINEAR: 'linear',
    EXPONENTIAL: 'exponential',
} as const;
type IBackoffStrategy = (typeof BackoffStrategy)[keyof typeof BackoffStrategy];

/** Returns an amount of millisecond. */
type BackoffStrategyFn = (attempt: number) => number;

export interface Options<R, E> {
    attempts: number;
    backoffStrategy: IBackoffStrategy | BackoffStrategyFn;
    isSuccess: (_res: R, _attempt: number) => boolean;

    /** If this predicate returns false, `retryResult` fails with RetryError.SHOULD_NOT_RETRY_AFTER_SUCCESS */
    shouldRetrySuccess: (_res: R, _attempt: number) => boolean;

    /** If this predicate returns false, `retryResult` fails with RetryError.SHOULD_NOT_RETRY_AFTER_ERROR */
    shouldRetryError: (_error: E, _attempt: number) => boolean;

    minDelayInMs: TimeInMilliseconds;
    maxDelayInMs: TimeInMilliseconds;
}

const defaultOptions: Options<unknown, unknown> = {
    attempts: 3,
    backoffStrategy: BackoffStrategy.LINEAR,
    isSuccess: () => true,
    shouldRetrySuccess: () => true,
    shouldRetryError: () => true,
    minDelayInMs: 1 * TimeInMilliseconds.SECOND,
    maxDelayInMs: 1 * TimeInMilliseconds.MINUTE,
};

const defaultBackoffStrategyFns: Record<IBackoffStrategy, BackoffStrategyFn> = {
    [BackoffStrategy.CONSTANT]: () => 5 * TimeInMilliseconds.SECOND,
    [BackoffStrategy.LINEAR]: (attempt) => attempt * 5 * TimeInMilliseconds.SECOND,
    [BackoffStrategy.EXPONENTIAL]: (attempt) => 2 ** attempt * TimeInMilliseconds.SECOND,
};

function computeDelay(
    attempt: number,
    backoffStrategy: IBackoffStrategy | BackoffStrategyFn,
    minDelayInMs: number,
    maxDelayInMs: number
): TimeInMilliseconds {
    const isCustomFn = typeof backoffStrategy === 'function';
    const res = isCustomFn ? backoffStrategy(attempt) : defaultBackoffStrategyFns[backoffStrategy](attempt);
    return Math.min(Math.max(res, minDelayInMs), maxDelayInMs);
}

/**
 * Try (and retry) the given Fn with extensive configuration.
 *
 * This version expects a Fn that return a Result type from 'neverthow' (and obviously that do not throw).
 * Thus, the Error becomes typed.
 *
 * Default options: check defaultOptions object
 */
export async function retryResult<R, E>(
    fn: () => Promise<Result<R, E>>,
    optionsParam?: Partial<Options<NoInfer<R>, NoInfer<E>>>
): Promise<Result<R, RetryErrorObject<R, E>>> {
    const options: Options<R, E> = { ...defaultOptions, ...(optionsParam ?? {}) };
    if (options.attempts < 1) {
        throw new Error('attempts must be greater than 0');
    }
    let lastResult: Result<R, E> | undefined;
    for (let attempt = 1; attempt <= options.attempts; attempt += 1) {
        lastResult = await fn();
        if (lastResult.isErr()) {
            const shouldRetryError = options.shouldRetryError(lastResult.error, attempt);
            if (!shouldRetryError) {
                return err({ error: RetryError.SHOULD_NOT_RETRY_AFTER_ERROR, lastResult: err(lastResult.error) });
            }
        } else {
            const isSuccess = options.isSuccess(lastResult.value, attempt);
            if (isSuccess) {
                return ok(lastResult.value);
            }
            const shouldRetrySuccess = options.shouldRetrySuccess(lastResult.value, attempt);
            if (!shouldRetrySuccess) {
                return err({ error: RetryError.SHOULD_NOT_RETRY_AFTER_SUCCESS, lastResult: ok(lastResult.value) });
            }
        }
        const delay = computeDelay(attempt, options.backoffStrategy, options.minDelayInMs, options.maxDelayInMs);
        if (attempt + 1 <= options.attempts) {
            await waitFor(delay);
        }
    }
    if (!lastResult) {
        throw new Error('res is set because the loop iterates at least once because options.attempts >= 1');
    }
    return err({ error: RetryError.STILL_ERROR_AFTER_RETRIES, lastResult });
}

/**
 * Try (and retry) the given Fn with extensive configuration.
 *
 * This version expects a Fn that can throw.
 * The Error type is 'unknown'.
 *
 * Default options: check defaultOptions object
 */
export async function retry<R>(
    fn: () => Promise<R>,
    optionsParam?: Partial<Options<NoInfer<R>, unknown>>
): Promise<Result<R, RetryErrorObject<R, unknown>>> {
    const options: Options<R, unknown> = { ...defaultOptions, ...(optionsParam ?? {}) };
    if (options.attempts < 1) {
        throw new Error('attempts must be greater than 0');
    }
    let lastError: unknown;
    let lastResult: R | undefined;
    let lastResultType: 'ok' | 'error' | undefined;
    for (let attempt = 1; attempt <= options.attempts; attempt += 1) {
        lastResultType = undefined;
        try {
            lastResult = await fn();
            lastResultType = 'ok';
            const isSuccess = options.isSuccess(lastResult, attempt);
            if (isSuccess) {
                return ok(lastResult);
            }
            const shouldRetrySuccess = options.shouldRetrySuccess(lastResult, attempt);
            if (!shouldRetrySuccess) {
                return err({ error: RetryError.SHOULD_NOT_RETRY_AFTER_SUCCESS, lastResult: ok(lastResult) });
            }
        } catch (error: unknown) {
            lastResultType = 'error';
            lastError = error;
            const shouldRetryError = options.shouldRetryError(error, attempt);
            if (!shouldRetryError) {
                return err({ error: RetryError.SHOULD_NOT_RETRY_AFTER_ERROR, lastResult: err(error) });
            }
        }
        const delay = computeDelay(attempt, options.backoffStrategy, options.minDelayInMs, options.maxDelayInMs);
        if (attempt + 1 <= options.attempts) {
            await waitFor(delay);
        }
    }
    if (lastResultType === 'error') {
        return err({ error: RetryError.STILL_ERROR_AFTER_RETRIES, lastResult: err(lastError) });
    }
    return err({ error: RetryError.STILL_ERROR_AFTER_RETRIES, lastResult: ok(lastResult as R) });
}
